import * as z from "zod/mini";
import {
  APPLE_PAY_BUTTON_DIMENSION_KEYS,
  APPLE_PAY_BUTTON_LOCALES,
  APPLE_PAY_BUTTON_STYLES,
  APPLE_PAY_BUTTON_TYPES,
  type ApplePayButtonSettings,
} from "@connect/component-library/components/apple-pay-button";
import { dimensionSchema, normalizeDimension, parseSettingsJson } from "./button-settings-common";

// Malformed top-level shapes (arrays, primitives, null) fail and fall back to `{}`;
// a bad individual field falls back via its own `z.catch` without discarding its
// valid siblings. Passing the const arrays straight to z.enum keeps the literal types.
const applePayButtonSettingsSchema = z.catch(
  z.object({
    width: dimensionSchema,
    height: dimensionSchema,
    borderRadius: dimensionSchema,
    paddingX: dimensionSchema,
    paddingY: dimensionSchema,
    style: z.catch(z.optional(z.enum(APPLE_PAY_BUTTON_STYLES)), undefined),
    type: z.catch(z.optional(z.enum(APPLE_PAY_BUTTON_TYPES)), undefined),
    locale: z.catch(z.optional(z.enum(APPLE_PAY_BUTTON_LOCALES)), undefined),
  }),
  {},
);

/** Parses and validates the `applePayButtonSettings` query param (JSON, URL-encoded). */
export function parseApplePayButtonSettings(
  raw: string | null | undefined,
): ApplePayButtonSettings | undefined {
  const parsed = parseSettingsJson(raw);
  if (parsed === undefined) {
    return undefined;
  }

  const result = z.safeParse(applePayButtonSettingsSchema, parsed);
  if (!result.success) {
    return undefined;
  }

  const settings: ApplePayButtonSettings = {};

  for (const key of APPLE_PAY_BUTTON_DIMENSION_KEYS) {
    const normalized = normalizeDimension(result.data[key]);
    if (normalized) {
      settings[key] = normalized;
    }
  }

  if (result.data.style) {
    settings.style = result.data.style;
  }
  if (result.data.type) {
    settings.type = result.data.type;
  }
  if (result.data.locale) {
    settings.locale = result.data.locale;
  }

  return Object.keys(settings).length > 0 ? settings : undefined;
}
